//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import {
  IAugmentedJQuery,
  IControllerConstructor,
  Injectable,
  IScope,
} from 'angular';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { ThemeProvider } from 'styled-components';

import { ICuralateStore } from '@models/AppState';
import { ReactQueryProvider } from '@queries/ReactQueryProvider';

/**
 * Creates an Angular Component Controller that initializes a new React App with the given component
 * @param function to create component React Component/HTML to initialize with access to Angular scope
 */
export default function makeAngularReactControllerWithScope<
  T = Record<string, unknown>,
>(
  componentFn: (scope: IScope & T) => React.ReactNode,
): Injectable<IControllerConstructor> {
  return [
    '$ngRedux',
    '$element',
    '$scope',
    (
      $ngRedux: Store<ICuralateStore>,
      $element: IAugmentedJQuery,
      $scope: IScope,
    ) => {
      const node = $element[0] ?? null;
      let root: Root | null = null;

      const renderComponent = () => {
        const element = (
          <ThemeProvider theme={theme}>
            <Provider store={$ngRedux}>
              <ReactQueryProvider>
                {componentFn($scope as IScope & T)}
              </ReactQueryProvider>
            </Provider>
          </ThemeProvider>
        );

        // Root is created once per controller instance. The node reference
        // is stable for the controller's lifetime, and cleanup happens in $onDestroy.
        if (!root && node) {
          root = createRoot(node);
        }

        if (root) {
          root.render(element);
        }
      };

      // Ensure the component is rendered at least once initially.
      renderComponent();

      return {
        $onChanges: renderComponent,
        $onDestroy: () => {
          if (root) {
            root.unmount();
            root = null;
          }
        },
      };
    },
  ];
}

/**
 * Apply Async Angular scope to a function from the React component so it triggers a digest when called.
 * @param scope An Angular scope for the element
 * @param node A react node
 */
export function applyAsyncScope(scope: IScope, func: any) {
  return typeof func === 'function'
    ? function (this: unknown, ...args: unknown[]) {
        return scope.$applyAsync(() => func.apply(this, args));
      }
    : func;
}

/**
 * Apply Angular scope to a function from the React component so it triggers a digest when called.
 * @param scope An Angular scope for the element
 * @param node A react node
 */
export function applyScope(scope: IScope, func: any) {
  return typeof func === 'function'
    ? function (this: unknown, ...args: unknown[]) {
        return scope.$apply(() => func.apply(this, args));
      }
    : func;
}
